/** Session flag while user must finish setting a new password after email link. */
export const PASSWORD_RECOVERY_PENDING_KEY = "autoplus_password_recovery_pending";

export function markPasswordRecoveryPending(): void {
  try {
    sessionStorage.setItem(PASSWORD_RECOVERY_PENDING_KEY, "1");
  } catch {
    /* private browsing */
  }
}

export function clearPasswordRecoveryPending(): void {
  try {
    sessionStorage.removeItem(PASSWORD_RECOVERY_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function isPasswordRecoveryPending(): boolean {
  try {
    return sessionStorage.getItem(PASSWORD_RECOVERY_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

/** True when the URL carries Supabase recovery tokens (hash or query). */
export function urlHasRecoveryCallback(): boolean {
  if (typeof window === "undefined") return false;

  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  if (hashParams.get("type") === "recovery") return true;

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get("type") === "recovery") return true;

  return false;
}

/** PKCE recovery may arrive as ?code= on the wrong path when redirect_to is misconfigured. */
export function redirectMisplacedRecoveryCode(): boolean {
  if (typeof window === "undefined") return false;

  const path = window.location.pathname;
  if (path.startsWith("/reset-password") || path.startsWith("/auth/callback")) return false;

  const code = new URLSearchParams(window.location.search).get("code");
  if (!code) return false;

  markPasswordRecoveryPending();
  window.location.replace(`/reset-password?code=${encodeURIComponent(code)}`);
  return true;
}

/**
 * Run before React mounts. Sends recovery callbacks to /reset-password so the
 * temporary session is not consumed on the home page or login screen.
 */
export function redirectRecoveryCallbackToResetPage(): boolean {
  if (typeof window === "undefined") return false;

  if (redirectMisplacedRecoveryCode()) return true;

  if (!urlHasRecoveryCallback()) return false;

  markPasswordRecoveryPending();

  if (window.location.pathname.startsWith("/reset-password")) {
    return false;
  }

  const target = `/reset-password${window.location.search}${window.location.hash}`;
  window.location.replace(target);
  return true;
}

export function passwordRecoveryAllowedPath(path: string): boolean {
  return path.startsWith("/reset-password") || path.startsWith("/forgot-password");
}
